import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ChevronRight, PackageCheck, RefreshCw, Search } from "lucide-react";
import { Container, meta } from "@/components/store/page-parts";
import { useStore } from "@/lib/store-context";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/account/orders/")({
  head: () => meta("My Orders — betteraccess.store", "Track your digital orders and access delivery details."),
  component: Orders,
});

function Orders() {
  const { user } = useStore();
  const [orders, setOrders] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchOrders = async (query?: string) => {
    setLoading(true);
    try {
      const q = query ?? search ?? user?.email ?? "";
      if (q) {
        const res = await fetch(`/api/orders/track?query=${encodeURIComponent(q.trim())}`);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.orders)) {
            setOrders(data.orders);
          }
        }
      } else if (user?.id) {
        const res = await fetch(`/api/auth/me?userId=${encodeURIComponent(user.id)}`);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.orders)) {
            setOrders(data.orders);
          }
        }
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      setSearch(user.email);
      void fetchOrders(user.email);
    }
  }, [user]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void fetchOrders();
  };

  return (
    <Container className="py-8 sm:py-10">
      <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-extrabold uppercase text-primary">Track &amp; View Orders</p>
          <h1 className="mt-2 text-3xl font-black sm:text-4xl">Order History</h1>
          <p className="mt-2 text-sm text-muted-foreground">Search by your Order ID or email address to access delivery details.</p>
        </div>
        <form onSubmit={handleSearchSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 size-4 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Enter Order ID or email..."
              className="h-10 w-full rounded-md border bg-background pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary sm:w-72"
            />
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? <RefreshCw className="size-4 animate-spin" /> : "Track"}
          </Button>
        </form>
      </div>

      <div className="mt-8 overflow-hidden rounded-lg border bg-card">
        {orders.length > 0 ? (
          orders.map(o => (
            <Link
              key={o.id}
              to="/account/orders/$orderId"
              params={{ orderId: o.id }}
              className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border-b p-3 sm:flex sm:gap-4 sm:p-4 last:border-0 hover:bg-secondary/60"
            >
              <span className="grid size-11 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
                <PackageCheck />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-sm font-bold">{o.product_name}</h2>
                <p className="line-clamp-2 text-[10px] text-muted-foreground sm:text-xs">
                  {o.id} · Qty: {o.quantity} · {new Date(o.created_at).toLocaleDateString()}
                </p>
              </div>
              <span
                className={`hidden rounded-sm px-2 py-1 text-[10px] font-extrabold uppercase sm:block ${
                  o.fulfillment_status === "delivered"
                    ? "bg-success/10 text-success"
                    : o.payment_status === "paid"
                    ? "bg-blue-500/10 text-blue-500"
                    : "bg-amber-500/10 text-amber-500"
                }`}
              >
                {o.fulfillment_status === "delivered" ? "Delivered" : o.payment_status === "paid" ? "Processing" : "Pending"}
              </span>
              <strong className="text-xs sm:text-sm">
                {o.currency === "USD" ? `$${o.total_amount.toFixed(2)}` : `৳${o.total_amount.toFixed(0)}`}
              </strong>
              <ChevronRight className="hidden size-4 text-muted-foreground sm:block" />
            </Link>
          ))
        ) : (
          <div className="py-16 text-center text-muted-foreground">
            <PackageCheck className="mx-auto size-8 opacity-40" />
            <p className="mt-3 font-semibold">No orders found</p>
            <p className="mt-1 text-xs">Enter your email or Order ID above to look up your purchases.</p>
          </div>
        )}
      </div>
    </Container>
  );
}
